import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, RotateCcw, CheckCircle2, X as XIcon, PenTool } from "lucide-react";

/**
 * DeliveryProofCapture
 * - Photo: native <input type="file" capture="environment"> → resized & converted to JPEG data URL.
 * - Signature: HTML5 canvas with pointer events → PNG data URL.
 * - Receiver name: free-text, defaults to the receiver on file.
 * - onConfirm(payload) when both proof + signature are present.
 */
export default function DeliveryProofCapture({
  defaultReceiverName = "",
  onCancel,
  onConfirm,
  busy = false,
}) {
  const [photo, setPhoto] = useState(null);             // jpeg data URL
  const [signature, setSignature] = useState(null);     // png data URL (set on pointer-up)
  const [receiverName, setReceiverName] = useState(defaultReceiverName);
  const [drawing, setDrawing] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  // ---------- Photo ----------
  const onPhotoSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const dataUrl = await fileToCompressedJpeg(f, 1280, 0.78);
    setPhoto(dataUrl);
    e.target.value = ""; // allow re-selecting same file
  };
  const clearPhoto = () => setPhoto(null);

  // ---------- Signature canvas ----------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0f172a";
  }, []);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left,
      y: (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top,
    };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const onPointerMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getCanvasPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const onPointerUp = () => {
    if (!drawing) return;
    setDrawing(false);
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setSignature(dataUrl);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const canConfirm = !!photo && !!signature && !busy;

  return (
    <div data-testid="delivery-proof-panel" className="space-y-4">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-amber-300 mb-1">
          / Delivery Proof
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-400">
          Snap a photo and have the receiver sign before we mark this delivered.
        </div>
      </div>

      {/* Photo */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-900/50 p-3">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-600 dark:text-slate-400 flex items-center gap-2">
            <Camera className="w-3.5 h-3.5" /> Package Photo
          </Label>
          {photo && (
            <button
              data-testid="proof-photo-retake"
              type="button"
              onClick={clearPhoto}
              className="text-xs flex items-center gap-1 text-cyan-300 hover:text-cyan-200"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Retake
            </button>
          )}
        </div>
        {photo ? (
          <img
            src={photo}
            alt="Delivery proof"
            data-testid="proof-photo-preview"
            className="w-full rounded-md border border-slate-300 dark:border-slate-700 aspect-[4/3] object-cover"
          />
        ) : (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="proof-photo-input"
              onChange={onPhotoSelect}
            />
            <Button
              type="button"
              data-testid="proof-photo-btn"
              onClick={() => fileRef.current?.click()}
              className="w-full h-14 bg-white dark:bg-slate-950 border border-dashed border-slate-300 dark:border-slate-700 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
            >
              <Camera className="w-5 h-5 mr-2" /> Take Photo
            </Button>
          </>
        )}
      </div>

      {/* Signature */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-900/50 p-3">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-600 dark:text-slate-400 flex items-center gap-2">
            <PenTool className="w-3.5 h-3.5" /> Receiver Signature
          </Label>
          {signature && (
            <button
              data-testid="proof-sig-clear"
              type="button"
              onClick={clearSignature}
              className="text-xs flex items-center gap-1 text-cyan-300 hover:text-cyan-200"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
        <div className="rounded-md bg-white aspect-[5/2] overflow-hidden touch-none">
          <canvas
            ref={canvasRef}
            data-testid="proof-sig-canvas"
            className="w-full h-full block cursor-crosshair"
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={onPointerUp}
          />
        </div>
        <div className="mt-3 space-y-1.5">
          <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
            Receiver Name
          </Label>
          <Input
            data-testid="proof-receiver-name"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            placeholder="Who signed?"
            className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white h-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sticky bottom-0 bg-white dark:bg-slate-950 pt-2">
        <Button
          type="button"
          data-testid="proof-cancel-btn"
          variant="outline"
          onClick={onCancel}
          disabled={busy}
          className="h-12 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
        >
          <XIcon className="w-4 h-4 mr-2" /> Cancel
        </Button>
        <Button
          type="button"
          data-testid="proof-confirm-btn"
          onClick={() =>
            onConfirm({
              proof_photo: photo,
              signature_image: signature,
              receiver_name: receiverName.trim(),
            })
          }
          disabled={!canConfirm}
          className="h-12 bg-green-400 text-slate-950 hover:bg-green-300 disabled:bg-slate-200 disabled:dark:bg-slate-800 disabled:text-slate-500 font-bold uppercase tracking-wider"
        >
          <CheckCircle2 className="w-5 h-5 mr-2" /> Mark Delivered
        </Button>
      </div>
    </div>
  );
}

// Resize + JPEG-compress a File so we don't send a multi-MB photo as base64.
async function fileToCompressedJpeg(file, maxDim = 1280, quality = 0.78) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}
